import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Menu, X, Phone } from "lucide-react";

const navLinks = [
  { label: "Services", href: "/#services" },
  { label: "Testimonials", href: "/#testimonials" },
  { label: "About", href: "/#about" },
  { label: "Contact", href: "/#contact" },
  { label: "Careers", href: "/careers" },
  { label: "Shifts", href: "/shifts" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      if (location.pathname !== "/") {
        window.location.href = href;
      } else {
        const el = document.getElementById(href.replace("/#", ""));
        el?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.location.href = href;
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-lg border-b border-stone-100 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_iv-medics-clinic/artifacts/46lyldln_Blue%20Green%20Modern%20Medical%20Centre%20Logo_20260215_170034_0000.png"
              alt="IV Medics & Diagnostic Clinic Logo"
              className="w-12 h-12 rounded-full object-contain"
            />
            <div className="leading-tight">
              <span className="font-[Outfit] font-bold text-stone-900 text-sm sm:text-base block">
                IV Medics
              </span>
              <span className="text-[10px] text-stone-500 hidden sm:block">
                & Diagnostic Clinic
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.label}
                data-testid={`nav-${link.label.toLowerCase()}`}
                onClick={() => handleNavClick(link.href)}
                className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-green-700 transition-colors rounded-lg hover:bg-green-50"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:09162731000"
              className="flex items-center gap-2 text-sm text-stone-600 hover:text-green-700 transition-colors"
              data-testid="nav-phone"
            >
              <Phone className="w-4 h-4" />
              <span>09162731000</span>
            </a>
            <Link to="/book">
              <Button
                data-testid="nav-book-btn"
                className="bg-green-700 hover:bg-green-800 text-white rounded-full px-6 shadow-md hover:shadow-lg transition-all"
              >
                Book Appointment
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="sm" className="p-2" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6 text-stone-700" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] bg-white p-6">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex flex-col gap-2 mt-8">
                {navLinks.map((link) => (
                  <button
                    key={link.label}
                    onClick={() => handleNavClick(link.href)}
                    className="text-left px-4 py-3 text-stone-700 hover:bg-green-50 hover:text-green-700 rounded-xl transition-colors font-medium"
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </button>
                ))}
                <div className="border-t border-stone-100 mt-4 pt-4">
                  <Link to="/book" onClick={() => setOpen(false)}>
                    <Button
                      className="w-full bg-green-700 hover:bg-green-800 text-white rounded-full"
                      data-testid="mobile-book-btn"
                    >
                      Book Appointment
                    </Button>
                  </Link>
                  <a
                    href="tel:09162731000"
                    className="flex items-center justify-center gap-2 mt-3 text-sm text-stone-600"
                  >
                    <Phone className="w-4 h-4" /> 09162731000
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
